import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Breadcrumbs, Button, Link, Stack, Tab, Tabs, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getPolicies } from '../../../../api/endpoints/corporate';
import type { CorporatePolicy } from '../../../../api/types/corporate';
import { CBExceptionsPanel } from '../components/CBExceptionsPanel';
import { FTExceptionsPanel } from '../components/FTExceptionsPanel';
import { HBExceptionsPanel } from '../components/HBExceptionsPanel';
import { HotelReservationExceptionsPanel } from '../components/HotelReservationExceptionsPanel';

/**
 * Manages the 4 exception sub-types for a single policy. Looks the policy's name up from
 * the already-cached GetPolicies list (`corporate/policies` query key) instead of calling
 * Policy/GetPolicyById — that by-id route is confirmed to 500 with a server-side null
 * reference on this test account even for existing PolicyIDs, while GetPolicies itself
 * works fine, so reusing the list avoids depending on the broken route just to show a title.
 */
export default function PolicyExceptionsPage() {
  const { policyId } = useParams<{ policyId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const policyID = Number(policyId);

  const policiesQuery = useQuery({ queryKey: ['corporate', 'policies'], queryFn: () => getPolicies() });
  const policies: CorporatePolicy[] = policiesQuery.data?.policies ?? [];
  const policy = policies.find((p) => p.policyID === policyID);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/corporate/policy')}
          underline="hover"
        >
          Seyahat Kural Setleri
        </Link>
        <Typography variant="body2" color="text.primary">
          İstisnalar
        </Typography>
      </Breadcrumbs>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/corporate/policy')}>
          Geri
        </Button>
        <Typography variant="h5">{policy ? `${policy.policyName} — İstisnalar` : `Politika #${policyId} — İstisnalar`}</Typography>
      </Stack>

      {!Number.isFinite(policyID) || policyID <= 0 ? (
        <Typography color="error">Geçersiz politika kimliği.</Typography>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Uçak Bileti (Rota)" />
            <Tab label="Kabin Sınıfı (Ülke)" />
            <Tab label="Saat Bazlı" />
            <Tab label="Otel Rezervasyonu" />
          </Tabs>

          {tab === 0 && <FTExceptionsPanel policyID={policyID} />}
          {tab === 1 && <CBExceptionsPanel policyID={policyID} />}
          {tab === 2 && <HBExceptionsPanel policyID={policyID} />}
          {tab === 3 && <HotelReservationExceptionsPanel policyID={policyID} />}
        </>
      )}
    </Box>
  );
}
